import React, { Component } from "react";
import { NavLink, withRouter } from "react-router-dom";
import { withStyles } from "@material-ui/core/styles";
import Badge from "@material-ui/core/Badge";
import { fetchJSON } from "helpers/axios";
import { connect } from "react-redux";

import IntlMessages from "util/IntlMessages";
import CustomScrollbars from "util/CustomScrollbars";
import "./StudentMenuList.css";

const StyledBadge = withStyles((theme) => ({
  badge: {
    right: -7,
    top: 3,
    fontSize: "10px",
    border: `1px solid ${theme.palette.background.paper}`,
    padding: "2px",
  },
}))(Badge);

const ACTIVE_CLASS = "student-nav-active";

// requiresProfile: locked until the student has updated their profile
const MENU_ITEMS = [
  { to: "/student/dashboard", icon: "zmdi-view-dashboard", label: <IntlMessages id="sidebar.dashboard" /> },
  { to: "/student/profile", icon: "zmdi-account-circle", label: "Profile" },
  { to: "/student/payment", icon: "zmdi-card", label: "Fees Payment", requiresProfile: true },
  { to: "/student/courseregistration", icon: "zmdi-collection-bookmark", label: "Courses Registration", requiresProfile: true },
  { to: "/student/applyforhostel", icon: "zmdi-hotel", label: "Apply for Hostel", requiresProfile: true },
  { to: "/student/ReportCard", icon: "zmdi-assignment", label: "Report Card", requiresProfile: true },
  { to: "/student/changepassword", icon: "zmdi-key", label: "Change Password" },
];

class StudentMenuList extends Component {
  constructor(props) {
    super(props);
    this.state = {
      notificationCounts: {
        lectureNotes: 0,
        assignments: 0,
        liveClassRoom: 0,
        learningDiscussion: 0,
        announcements: 0,
        exams: 0,
      },
    };
  }

  componentDidMount() {
    const { authUser } = this.props;
    if (authUser && authUser.StudId) {
      this.fetchAllNotifications();
    }
  }

  fetchAllNotifications = async () => {
    const { authUser } = this.props;
    if (!authUser || !authUser.StudId) return;

    const { StudId, token } = authUser;

    const notificationTypes = [
      { type: 2, key: "lectureNotes" },
      { type: 3, key: "assignments" },
      { type: 4, key: "liveClassRoom" },
      { type: 5, key: "learningDiscussion" },
      { type: 6, key: "announcements" },
      { type: 7, key: "exams" },
    ];

    await Promise.all(
      notificationTypes.map(({ type, key }) =>
        this.fetchNotificationCount(StudId, token, type, key)
      )
    );
  };

  fetchNotificationCount = async (studId, token, notificationType, stateKey) => {
    try {
      const response = await fetchJSON(
        `api/GetUnReadNotifications?staffId=${studId}&notificationType=${notificationType}`,
        null,
        "GET",
        token
      );

      if (response?.Status === true) {
        this.setState((prev) => ({
          notificationCounts: {
            ...prev.notificationCounts,
            [stateKey]: response?.Data?.length || 0,
          },
        }));
      }
    } catch (error) {}
  };

  markNotificationsAsRead = async (notificationType) => {
    const { authUser } = this.props;
    if (!authUser || !authUser.StudId) return;

    const { StudId, token } = authUser;

    const response = await fetchJSON(
      `api/MarkNotificationsAsRead?staffId=${StudId}&notificationType=${notificationType}`,
      null,
      "POST",
      token
    );

    if (response?.Status === true) {
      this.setState((prev) => ({
        notificationCounts: {
          ...prev.notificationCounts,
          [this.getKeyFromType(notificationType)]: 0,
        },
      }));
    }
  };

  getKeyFromType = (type) => {
    const mapping = {
      2: "lectureNotes",
      3: "assignments",
      4: "liveClassRoom",
      5: "learningDiscussion",
      6: "announcements",
      7: "exams",
    };
    return mapping[type] || "";
  };

  renderMenuItemWithBadge = (to, icon, text, notificationType) => {
    const count =
      this.state.notificationCounts[this.getKeyFromType(notificationType)] || 0;

    return (
      <NavLink
        to={to}
        activeClassName={ACTIVE_CLASS}
        onClick={() => this.markNotificationsAsRead(notificationType)}
      >
        {count > 0 ? (
          <StyledBadge badgeContent={count} color="secondary">
            <i className={`zmdi ${icon} zmdi-hc-fw`} />
            <span className="nav-text">{text}</span>
          </StyledBadge>
        ) : (
          <>
            <i className={`zmdi ${icon} zmdi-hc-fw`} />
            <span className="nav-text">{text}</span>
          </>
        )}
      </NavLink>
    );
  };

  /** Menu link; disabled items stay visible but can't be opened */
  renderMenuItem = ({ to, icon, label }, disabled) => (
    <li className="menu no-arrow" key={to}>
      <NavLink
        to={to}
        activeClassName={ACTIVE_CLASS}
        className={disabled ? "student-nav-disabled" : undefined}
        aria-disabled={disabled || undefined}
        title={disabled ? "Update your profile to unlock this page" : undefined}
        onClick={disabled ? (e) => e.preventDefault() : undefined}
      >
        <i className={`zmdi ${icon} zmdi-hc-fw`} />
        <span className="nav-text">{label}</span>
        {disabled && <i className="zmdi zmdi-lock-outline student-nav-lock" />}
      </NavLink>
    </li>
  );

  render() {
    const { authUser } = this.props;
    const isProfileUpdated = authUser?.ProfileUpdated === true;

    return (
      <CustomScrollbars className="scrollbar">
        <ul className="nav-menu student-nav">
          <li className="nav-header">
            <IntlMessages id="sidebar.menu" />
          </li>

          {MENU_ITEMS.map((item) =>
            this.renderMenuItem(item, item.requiresProfile && !isProfileUpdated)
          )}

          {/* Not in use yet:
              this.renderMenuItemWithBadge("/student/liveclasses", "zmdi-videocam", "Live Classes", 4)
              this.renderMenuItemWithBadge("/student/exams", "zmdi-edit", "Exams", 7)
              this.renderMenuItemWithBadge("/student/lecturenote", "zmdi-file-text", "Lecturer Note", 2)
              this.renderMenuItemWithBadge("/student/discussion", "zmdi-comments", "Discussion", 5)
              this.renderMenuItemWithBadge("/student/assignment", "zmdi-assignment-check", "Assignment", 3) */}
        </ul>
      </CustomScrollbars>
    );
  }
}

const mapStateToProps = ({ auth }) => ({
  authUser: auth.studentUser,
});

export default withRouter(connect(mapStateToProps)(StudentMenuList));
